// Single source of truth for the site's public, factual content.
//
// Both the human pages (homepage, services index, guides index) and the
// machine surfaces (llms.txt, markdown twins, the public JSON API, the public
// MCP server, /for-agents) read from here, so the two can never disagree.
// Browser-safe: no server imports, no env access.

export const SITE = {
  name: "the Vibing Company",
  legalName: "the Vibing Company (Jonathan Osburg)",
  url: "https://vibing.inc",
  tagline:
    "An external product team of full-stack designers. We take websites and web apps from idea to live product in 14 days for a fixed €35,000: product strategy, UI/UX and the whole build, with brand, motion and growth work from the same studio.",
  email: "jon@vibing.inc",
  location: "Penzberg, Bavaria, Germany. Remote-first, worldwide",
  founder: "Jon Osburg, one of only two official Lovable Super Champions",
  proof: "50+ products shipped · 25+ teams served · 45,000+ users migrated",
  bookingUrl: "https://cal.com/vibinc/30min",
} as const;

export type ContentEntry = {
  /** Path without leading slash segment duplication, e.g. "/services/mvp-development". */
  path: string;
  title: string;
  summary: string;
  /** Extra machine-readable facts surfaced in markdown twins and the JSON API. */
  facts?: Record<string, string>;
};

export const SERVICE_PAGES: ContentEntry[] = [
  {
    path: "/services/mvp-development",
    title: "MVP development",
    summary:
      "From idea to live product in 14 days for a fixed €35,000. Product strategy, UI/UX and the whole build from one team of full-stack designers. Websites and web apps, built to a studio standard.",
    facts: { price: "€35,000 fixed", turnaround: "14 days" },
  },
  {
    path: "/services/lovable-development",
    title: "Product design & development",
    summary:
      "Full-stack designers and vetted Lovable experts who own your product end to end, in bi-weekly sprints you can pause anytime.",
    facts: { audience: "Companies past launch who want an external product team on the roadmap", pricing: "Scoped on the call" },
  },
  {
    path: "/services/vibe-coding-rescue",
    title: "Vibe coding rescue",
    summary:
      "You vibe-coded into a corner. We diagnose, refactor and unstick stalled Lovable projects, usually inside a week.",
    facts: { typicalTurnaround: "Within one week" },
  },
  {
    path: "/services/app-development-consultant",
    title: "App development consulting",
    summary:
      "Senior advisory for founders and product teams. Architecture reviews, build/buy calls, hiring help, second opinions.",
  },
  {
    path: "/services/lovable-cloud-migration",
    title: "Lovable Cloud migration",
    summary:
      "Move off Lovable Cloud onto your own Supabase. Row-level security, payments, edge functions, full ownership.",
  },
];

export const GUIDE_PAGES: ContentEntry[] = [
  {
    path: "/guides/what-is-vibe-coding",
    title: "What is vibe coding?",
    summary:
      "The honest 2026 explainer: origins, tools, what it's great at, where it breaks, and how to ship vibe-coded apps you'd trust in production.",
    facts: { kind: "Cornerstone guide" },
  },
  {
    path: "/guides/lovable-vs-cursor",
    title: "Lovable vs Cursor vs Claude Code vs Codex",
    summary:
      "Side-by-side comparison of the four AI coding tools that actually matter in 2026: features, pricing, models, and which one to pick.",
    facts: { kind: "Tool comparison" },
  },
  {
    path: "/guides/ios-safari-white-bar-fix",
    title: "The iOS 26 Safari white bar, explained",
    summary:
      "Why Safari paints a white bar over the notch, why your sticky header is the culprit, and the tested recipe for a true edge-to-edge website on iPhone.",
    facts: { kind: "Battle report" },
  },
  {
    path: "/guides/how-to-build-an-mcp-server-with-lovable",
    title: "How to build an MCP server with Lovable",
    summary:
      "What MCP actually is, when it's worth building, the three files you write in Lovable, and the auth decision that decides whether the whole thing is safe.",
    facts: { kind: "Build guide" },
  },
  {
    path: "/guides/export-data-from-lovable-cloud",
    title: "How to export your data from Lovable Cloud",
    summary:
      "The exact click path for a database export, the 5 GB and 24-hour limits, what the dump leaves behind, and how to restore it into your own Supabase.",
    facts: { kind: "Migration guide" },
  },
];

/**
 * The one public offer. Everything commercial on the site (homepage pricing,
 * service pages, llms.txt, the JSON API) reads from here.
 */
export const OFFER = {
  id: "idea-to-product-14",
  name: "Idea to live product",
  price: "€35,000",
  cadence: "fixed",
  days: 14,
  summary:
    "A website or web app taken from idea to live product in 14 days. Product strategy, UI/UX and the whole build by one team of full-stack designers. One price, one launch date, you own everything.",
  audience: "Companies that need to prove an idea fast and scale it faster.",
  builtFor: ["Websites and web apps", "Funded startups", "New product lines", "Corporate ventures"],
  includes: [
    "Product strategy and a locked scope on day 1",
    "UI/UX design and a design system built for growth",
    "The whole build: accounts, data, payments, integrations",
    "Performance, accessibility and SEO pass before launch",
    "Live on your domain on day 14",
    "Handover of the code, design files and domain. All yours",
    "Slack, plus one call a week",
  ],
} as const;

/**
 * The studio around the sprint. Creative work companies add to the 14 days
 * or book after launch. No public prices; each one is scoped on the call.
 */
export const STUDIO_ADDONS = [
  {
    id: "brand",
    name: "Brand identity",
    blurb: "Logo, type, color and voice, designed alongside the product so both ship together.",
  },
  {
    id: "launch-site",
    name: "Launch website",
    blurb: "A marketing site that sells what you just built. Fast, searchable, on brand.",
  },
  {
    id: "motion",
    name: "Motion and 3D",
    blurb: "Product animation, rendered objects and micro-interactions that make it feel expensive.",
  },
  {
    id: "film",
    name: "Product film",
    blurb: "A short launch video for your homepage, socials and the next investor update.",
  },
  {
    id: "decks",
    name: "Pitch and sales decks",
    blurb: "The story of the product, designed to the same standard as the product.",
  },
  {
    id: "sprints",
    name: "Ongoing sprints",
    blurb: "Keep the team after launch. Two-week sprints on a roadmap you own, pause anytime.",
  },
] as const;

/** Flat list for machine surfaces (llms.txt, the public JSON API). */
export const PRICING = [
  {
    id: OFFER.id,
    name: `${OFFER.name} in ${OFFER.days} days`,
    price: OFFER.price,
    cadence: OFFER.cadence,
    summary: OFFER.summary,
  },
] as const;

export const CORE_PAGES: ContentEntry[] = [
  { path: "/", title: "the Vibing Company", summary: SITE.tagline },
  { path: "/services", title: "Services", summary: "All five ways to work with us." },
  {
    path: "/work",
    title: "Work",
    summary:
      "Case studies of live products we designed and built: Outfitlink, mooody, moooments, Just Convert It, Unstatus, Easy Lover and more.",
  },
  { path: "/guides", title: "Guides", summary: "Independent guides on vibe coding and AI coding tools." },
  {
    path: "/library",
    title: "The Vibeary",
    summary:
      "The biggest public vibe-coding library: hand-curated tools, components, AI skills, shaders and inspiration, updated daily.",
  },
  { path: "/contact", title: "Contact", summary: "Four-step intake form. Human reply within 24 hours." },
  { path: "/hiring", title: "Hiring", summary: "Open roles and how we work." },
  { path: "/for-agents", title: "For agents", summary: "Machine-readable access to everything on this site." },
  {
    path: "/webmcp",
    title: "WebMCP",
    summary:
      "Browser-native agent tools: every page registers WebMCP tools on document.modelContext, with a live console to try them.",
  },
  {
    path: "/easy-lover",
    title: "Easy Lover: Customize Your Lovable Toolbar",
    summary:
      "Pin and reorder pages in Lovable's toolbar, keep GitHub settings beside Share, and save your layout locally with the Easy Lover Chrome extension.",
  },
  { path: "/docs", title: "Developer docs", summary: "Public API, MCP server, markdown twins and error shapes. No key required." },
];

export const LEGAL_PAGES: ContentEntry[] = [
  {
    path: "/easy-lover/privacy",
    title: "Easy Lover privacy policy",
    summary: "How the Easy Lover Chrome extension processes the Lovable editor locally and stores toolbar preferences.",
  },
  { path: "/imprint", title: "Imprint", summary: "Company details (Impressum, § 5 TMG)." },
  { path: "/privacy", title: "Privacy", summary: "Privacy policy." },
  { path: "/terms", title: "Terms", summary: "Terms of service." },
  { path: "/refunds", title: "Refunds", summary: "Refund policy." },
  { path: "/cookies", title: "Cookies", summary: "Cookie policy." },
];
